/**
 * PATH: packages/ui/src/kanban/components/KanbanCardDateChip.tsx
 * FILE_ID: UI-KANBAN-CARD-DATE-CHIP-001
 * PURPOSE: Click-to-edit wrapper for card-FACE dates: renders its children
 *   (the pill face) as a pressable chip that swaps in the shared
 *   SmartDateInput in place on click.
 * WHY: BUG/UAT 2026-07-06 — the operator clicked the date pill on the card
 *   expecting to change the action date; the pill was display-only, so
 *   "nothing happened". Editing existed only in the drawer + right-click
 *   menu. This makes EVERY date shown on a card the edit affordance itself,
 *   through the same SmartDateInput + patch pipe those paths use (no second
 *   write path, identical audit/undo via the host handler).
 * FLOW:
 *   ┌───────────┐   ┌──────────────────┐   ┌───────────────────────┐
 *   │ chip click│ → │ SmartDateInput   │ → │ onCommit(iso | null)  │
 *   │ (swallow) │   │ swap-in (focus)  │   │ → host patch + undo   │
 *   └───────────┘   └──────────────────┘   └───────────────────────┘
 * RELATED:
 *   - ./DateField.tsx (SmartDateInput — autoFocus/onDismiss are for us)
 *   - ./KanbanCardDuePills.tsx, ./KanbanCardActionLine.tsx (hosts)
 */
import { useState } from 'react'
import type { ReactNode } from 'react'

import { SmartDateInput } from './DateField'

/** Swallow pointer events so the (draggable) card root never sees them —
 *  a chip click must not open the drawer or start a drag. */
function swallow(e: { stopPropagation: () => void }) {
  e.stopPropagation()
}

export interface KanbanCardDateChipProps {
  /** Current ISO date (YYYY-MM-DD; datetime tolerated) or null. */
  value: string | null | undefined
  ariaLabel: string
  /** The write pipe. Omitted → children render untouched (read-only host:
   *  byte-identical to the pre-chip face). */
  onCommit?: (iso: string | null) => Promise<unknown> | void
  /** The pill face shown at rest. */
  children: ReactNode
  /** Extra classes on the rest-state button. */
  className?: string
}

export function KanbanCardDateChip({
  value,
  ariaLabel,
  onCommit,
  children,
  className = '',
}: KanbanCardDateChipProps) {
  const [editing, setEditing] = useState(false)

  if (!onCommit) return <>{children}</>

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={`${ariaLabel} — click to change`}
        title={`${ariaLabel} — click to change`}
        draggable={false}
        onMouseDown={swallow}
        onDragStart={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setEditing(true)
        }}
        data-testid="kanban-card-date-chip"
        className={[
          // Rest state mirrors the passive pill; hover reveals the edit
          // affordance (ring + underline) the static pill lacked.
          'inline-flex cursor-pointer items-start rounded px-0.5 py-0.5 text-left transition-colors',
          'hover:bg-neutral-200/70 hover:underline dark:hover:bg-zinc-800/70',
          className,
        ].join(' ')}
      >
        {children}
      </button>
    )
  }

  const current = value ? value.slice(0, 10) : null
  return (
    <span
      draggable={false}
      onMouseDown={swallow}
      onClick={swallow}
      onDragStart={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      data-testid="kanban-card-date-chip-editor"
    >
      <SmartDateInput
        ariaLabel={ariaLabel}
        placeholder="12/8 · week 6 · tomorrow"
        value={current}
        autoFocus
        onDismiss={() => setEditing(false)}
        onChange={(iso) => {
          setEditing(false)
          if (iso !== current) void onCommit(iso)
        }}
        className="w-[104px] rounded border border-blue-300 bg-white px-1.5 py-0.5 text-[10.5px] text-neutral-700 focus:outline-none focus:ring-1 focus:ring-blue-500/40 dark:border-blue-800 dark:bg-zinc-900 dark:text-neutral-200"
      />
    </span>
  )
}
